import "./ChatReasoningSection.css";
import {
	createEffect,
	createSignal,
	type JSX,
	onCleanup,
	Show,
} from "solid-js";
import { Button } from "@ui/Button.jsx";
import { HStack, VStack } from "@ui/Stack.jsx";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { TextMono } from "@ui/typography/TextMono.jsx";
import type { AnswerState } from "../chatPanelState.ts";
import { ThinkingHeading } from "./ThinkingHeading.tsx";
import { ThinkingTicker } from "./ThinkingTicker.tsx";
import { ThinkingImageTicker } from "./ThinkingImageTicker.tsx";
import { ThinkingProgress } from "./ThinkingProgress.tsx";
import { ThinkingLog } from "./ThinkingLog.tsx";
import { GVTSymbol } from "@ui/typography/GVTSymbol.tsx";
import { GVTThinkingSymbol } from "@ui/typography/GVTThinkingSymbol.tsx";

const ReasoningSymbol = (props: { thinking: boolean }) => (
	<Show when={props.thinking} fallback={<GVTSymbol>thinking</GVTSymbol>}>
		<GVTThinkingSymbol />
	</Show>
);

/** The `.accordion` transition in shared-core.css. */
const ACCORDION_CLOSE_MS = 300;

const ShowHideDetailsButton = (props: {
	open: boolean;
	thinking: boolean;
	onToggle: (open: boolean) => void;
}) => {
	return (
		<Button
			variant="ghost"
			theme="monochrome"
			class="toggle-reasoning-visibility"
			classList={{ open: props.open }}
			onClick={() => props.onToggle(!props.open)}
		>
			<HStack spacing="medium">
				<TextMono color="fg2">
					<GVTSymbol>thinking</GVTSymbol>{" "}
					<Show when={props.open} fallback={"Show details"}>
						Hide details
					</Show>
				</TextMono>
				<MaterialSymbol
					symbol="keyboard_arrow_down"
					class="expand"
					relativeSize="even-larger"
					color="fg4"
				/>
			</HStack>
		</Button>
	);
};

export const ChatReasoningSection = (props: {
	answer: AnswerState;
	interactionIndex: number;
	renderingHistory: boolean;
	reasoning: boolean;
	hasVisibleOutput: boolean;
	onToggleReasoning: (open: boolean) => void;
	onCloseReasoning: () => void;
	/**
	 * Shown ahead of the thinking steps, as the effort is settled before the
	 * agent takes its first one.
	 */
	effortUpgradeResolution?: JSX.Element;
}) => {
	const showLiveReasoning = () =>
		!props.answer.answerAvailable && !props.renderingHistory;

	// Animate the reasoning symbol while reasoning is happening and the answer
	// section shows no visible output yet (i.e. the ChatInteraction thinking
	// symbol isn't the one on display). Exactly one animated symbol at a time.
	const thinking = () =>
		!props.renderingHistory && props.reasoning && !props.hasVisibleOutput;

	// A closed accordion only hides its contents, and a finished answer's log
	// is dozens of headed steps sitting ahead of the answer in the page's text,
	// which is what crawlers and screen readers read first. So the log is only
	// in the page while open, plus long enough for the close to animate.
	const [logMounted, setLogMounted] = createSignal(
		props.answer.reasoningEventsOpen,
	);
	createEffect(() => {
		if (props.answer.reasoningEventsOpen) {
			setLogMounted(true);
			return;
		}
		const unmount = setTimeout(() => setLogMounted(false), ACCORDION_CLOSE_MS);
		onCleanup(() => clearTimeout(unmount));
	});

	return (
		<div
			class="reasoning"
			classList={{ "answer-available": props.answer.answerAvailable }}
		>
			<Show when={showLiveReasoning()}>
				<HStack spacing="medium" class="live-reasoning">
					<ReasoningSymbol thinking={thinking()} />
					<div class="live-reasoning__content">
						<VStack spacing="medium">
							<Button
								variant="ghost"
								theme="monochrome"
								class="live-thinking-toggle"
								classList={{ open: props.answer.reasoningEventsOpen }}
								aria-expanded={props.answer.reasoningEventsOpen}
								aria-label={
									props.answer.reasoningEventsOpen
										? "Hide thinking steps"
										: "Show thinking steps"
								}
								onClick={() =>
									props.onToggleReasoning(!props.answer.reasoningEventsOpen)
								}
							>
								<HStack spacing="medium" align="center" fullWidth>
									<ThinkingHeading
										startedAtMs={props.answer.startedAtMs}
										thinkingEndedAtMs={props.answer.thinkingEndedAtMs}
									/>
									<MaterialSymbol
										symbol="keyboard_arrow_down"
										class="expand"
										relativeSize="even-larger"
										color="fg4"
									/>
								</HStack>
							</Button>
							{props.effortUpgradeResolution}
							<ThinkingTicker titles={props.answer.tickerTitles} />
							<ThinkingImageTicker images={props.answer.tickerImages} />
							<ThinkingProgress progress={props.answer.liveProgress} />
						</VStack>
						<div
							class="live-thinking-steps accordion"
							classList={{ open: props.answer.reasoningEventsOpen }}
						>
							<div class="accordion-contents">
								<div class="live-thinking-steps__inner">
									<Show when={logMounted()}>
										<ThinkingLog
											live
											entries={props.answer.thinkingLog}
											plans={props.answer.plans}
											startedAtMs={props.answer.startedAtMs}
											thinkingEndedAtMs={props.answer.thinkingEndedAtMs}
										/>
									</Show>
								</div>
							</div>
						</div>
					</div>
				</HStack>
			</Show>
			<Show when={!showLiveReasoning()}>
				<VStack class="reasoning-summary" spacing="medium">
					<div class="toolbar">
						<ShowHideDetailsButton
							open={props.answer.reasoningEventsOpen}
							thinking={thinking()}
							onToggle={props.onToggleReasoning}
						/>
					</div>
					<div
						class="reasoning-events-container accordion"
						classList={{ open: props.answer.reasoningEventsOpen }}
					>
						<div class="accordion-contents">
							<VStack spacing="medium">
								<Show when={logMounted()}>{props.effortUpgradeResolution}</Show>
								<div class="reasoning-events">
									<div class="reasoning-events-inner">
										<Show when={logMounted()}>
											<ThinkingLog
												entries={props.answer.thinkingLog}
												plans={props.answer.plans}
												startedAtMs={props.answer.startedAtMs}
												thinkingEndedAtMs={props.answer.thinkingEndedAtMs}
											/>
										</Show>
										{/* <ChatPlanSection plans={props.answer.plans} />
									<ChatExecutionBlock executions={props.answer.executions} /> */}
									</div>
								</div>
							</VStack>
						</div>
					</div>
					{/* <div class="close-reasoning">
					<Button
						variant="text"
						theme="monochrome"
						class="close-reasoning"
						symbol="keyboard_arrow_up"
						symbolRelativeSize="larger"
						onClick={props.onCloseReasoning}
					/>
					<div class="divider" />
				</div> */}
				</VStack>
			</Show>
		</div>
	);
};
